import { z } from "zod"
import {
  MemberOnlineStatus,
  MemberPrivatePhotoAccess,
} from "@hornet-web-react/core/models/member.model"
import { ProfileId } from "@hornet-web-react/core/types/session"
import MessageReactionTypeModel from "./message-reaction-type.model"
import { head, propEq, reject } from "ramda"
import { v4 as uuidv4 } from "uuid"
import {
  ConversationId,
  LastMessageApiPayload,
} from "./inbox-conversations.model"
import {
  SendingDisabledReason,
  SendingDisabledState,
} from "@hornet-web-react/core/models/realtime-session.model"
import {
  HornetProfileId,
  QuickiesProfileId,
} from "@hornet-web-react/core/types"
import { ReactNode } from "react"
import { isStingable } from "@hornet-web-react/core/utils/is-stingable"

export const InboxType = z.enum([
  "identity_verified", // instead of primary - to be implemented
  "primary", // to be deprecated
  "requests", // to be deprecated
  "all", // default one
  "replied",
  "unread",
  "following",
  "within_50_km",
  "within_300_km",
])
export type InboxType = z.infer<typeof InboxType>

export type InboxFolderItemType = {
  type: InboxType
  title: string
  description: string
  isSelected: boolean
  isShowingDot: boolean
  icon?: ReactNode
}

export const BaseConversationMember = z.object({
  id: HornetProfileId,
  display_name: z.string().nullable(),
  age: z.number().nullable(),
  distance: z.number().nullable(),
  unread_messages_from: z.number(),
  explorer: z.boolean(),
  online: z.boolean(),
  favourite: z.boolean().optional(),
  fan: z.boolean().optional(),
  status_icon: z.string(),
  system_profile: z.boolean(),
  broadcast_profile: z.boolean(),
  account: z.object({
    username: z.string(),
    public: z.boolean(),
  }),
  last_online: z.coerce.date(),
  verification_level: z.number(),
})

export const MessageType = z.enum([
  "chat",
  "fav_forward",
  "heart",
  "sticker",
  "location",
  "permission_request",
  "permission_response",
  "share_photo",
  "award",
  "voice_note",
  "web_view",
  "deleted_message",
  "direct_video_call",
  "share_expiring_photo",
])
export type MessageType = z.infer<typeof MessageType>

export const ChatMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.chat),
  data: z.string(),
})
export type ChatMessageApiPayload = z.infer<typeof ChatMessageApiPayload>

export const FavForwardMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.fav_forward),
  data: z.object({
    member_id: HornetProfileId,
  }),
})
export type FavForwardMessageApiPayload = z.infer<
  typeof FavForwardMessageApiPayload
>

export const HeartMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.heart),
})
export type HeartMessageApiPayload = z.infer<typeof HeartMessageApiPayload>

export const StickerApiPayload = z.object({
  sticker: z.object({
    media_router: z.string().url(),
    reference: z.string(),
    product_internal_id: z.string(),
  }),
})
export type StickerApiPayload = z.infer<typeof StickerApiPayload>

export const StickerMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.sticker),
  data: StickerApiPayload,
})
export type StickerMessageApiPayload = z.infer<typeof StickerMessageApiPayload>

export const LocationMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.location),
  data: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
})
export type LocationMessageApiPayload = z.infer<
  typeof LocationMessageApiPayload
>

export const PermissionRequestMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.permission_request),
  data: z.object({
    permission: z.object({
      permission_type: z.string(),
      requestor_id: z.coerce.string(),
      owner_id: z.coerce.string(),
    }),
  }),
})
export type PermissionRequestMessageApiPayload = z.infer<
  typeof PermissionRequestMessageApiPayload
>

export const PermissionResponsePermissionState = z
  .literal("granted")
  .or(z.literal("rejected"))
export type PermissionResponsePermissionState = z.infer<
  typeof PermissionResponsePermissionState
>

export const PermissionResponseMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.permission_response),
  data: z.object({
    permission: z.object({
      permission_type: z.string(),
      requestor_id: z.coerce.string(),
      owner_id: z.coerce.string(),
      state: PermissionResponsePermissionState,
    }),
  }),
})
export type PermissionResponseMessageApiPayload = z.infer<
  typeof PermissionResponseMessageApiPayload
>

export const SharePhotoMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.share_photo),
  data: z.object({
    photo_id: z.coerce.string(),
    member_id: z.coerce.string(),
    thumb_retina_url: z.string().url(),
    photo_url: z.string().url(),
    title: z.string().optional(),
    action: z.string().optional(),
  }),
})
export type SharePhotoMessageApiPayload = z.infer<
  typeof SharePhotoMessageApiPayload
>

export const AwardMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.award),
  data: z.object({
    award_type_id: z.coerce.string(),
  }),
})
export type AwardMessageApiPayload = z.infer<typeof AwardMessageApiPayload>

export const VoiceNoteMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.voice_note),
  data: z.object({
    playback_url: z.string().url(),
    playback_duration: z.number(),
  }),
})
export type VoiceNoteMessageApiPayload = z.infer<
  typeof VoiceNoteMessageApiPayload
>

export const WebViewMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.web_view),
  data: z.object({
    title: z.string(),
    url: z.string().url(),
    photo: z.string().url(),
    campaign_id: z.coerce.string().optional(),
    broadcast_id: z.coerce.string().optional(),
    advertiser_id: z.coerce.string().optional(),
  }),
})
export type WebViewMessageApiPayload = z.infer<typeof WebViewMessageApiPayload>

export const DeletedMessageMessageApiPayload = z.object({
  created_at: z.coerce.date(),
  type: z.literal(MessageType.enum.deleted_message),
  data: z.string().nullable(),
})
export type DeletedMessageMessageApiPayload = z.infer<
  typeof DeletedMessageMessageApiPayload
>

export const BareMessageApiPayload = z.discriminatedUnion("type", [
  ChatMessageApiPayload,
  FavForwardMessageApiPayload,
  HeartMessageApiPayload,
  StickerMessageApiPayload,
  LocationMessageApiPayload,
  PermissionRequestMessageApiPayload,
  PermissionResponseMessageApiPayload,
  SharePhotoMessageApiPayload,
  AwardMessageApiPayload,
  VoiceNoteMessageApiPayload,
  WebViewMessageApiPayload,
  DeletedMessageMessageApiPayload,
])

export type BareMessageApiPayload = z.infer<typeof BareMessageApiPayload>

const CreateMessageWithReplyApiPayload = z.object({
  parent_message: z
    .object({
      message_sequence: z.string(),
      participant_id: z.string(),
    })
    .optional(),
})

export const CreateBareMessageApiPayload = z.discriminatedUnion("type", [
  ChatMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  FavForwardMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  HeartMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  StickerMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  LocationMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  PermissionRequestMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  PermissionResponseMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  SharePhotoMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  AwardMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  VoiceNoteMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  WebViewMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
  DeletedMessageMessageApiPayload.merge(CreateMessageWithReplyApiPayload),
])

export type CreateBareMessageApiPayload = z.infer<
  typeof CreateBareMessageApiPayload
>

export const MessageClientRef = z.string().brand("MessageClientRef")
export type MessageClientRef = z.infer<typeof MessageClientRef>

export const MessageReadState = z.literal("delivered").or(z.literal("read"))
export type MessageReadState = z.infer<typeof MessageReadState>

const BaseMessageApiPayload = z.object({
  id: z.coerce.string(),
  real_id: z.string().uuid(),
  created_at: z.coerce.date(),
  client_ref: MessageClientRef.nullable(),
  sender: HornetProfileId,
  recipient: HornetProfileId,
  state: MessageReadState,
  message_id: z.string(),
  is_deletable: z.boolean(),
})

const BaseMessageApiPayloadUnion = z.discriminatedUnion("type", [
  ChatMessageApiPayload.merge(BaseMessageApiPayload),
  FavForwardMessageApiPayload.merge(BaseMessageApiPayload),
  HeartMessageApiPayload.merge(BaseMessageApiPayload),
  StickerMessageApiPayload.merge(BaseMessageApiPayload),
  LocationMessageApiPayload.merge(BaseMessageApiPayload),
  PermissionRequestMessageApiPayload.merge(BaseMessageApiPayload),
  PermissionResponseMessageApiPayload.merge(BaseMessageApiPayload),
  SharePhotoMessageApiPayload.merge(BaseMessageApiPayload),
  AwardMessageApiPayload.merge(BaseMessageApiPayload),
  VoiceNoteMessageApiPayload.merge(BaseMessageApiPayload),
  WebViewMessageApiPayload.merge(BaseMessageApiPayload),
  DeletedMessageMessageApiPayload.merge(BaseMessageApiPayload),
])

const MasterMessageApiPayload = BaseMessageApiPayload.merge(
  z.object({
    parent_message: BaseMessageApiPayloadUnion.nullable().optional(), // `deleted_message` does not have the field
    reaction_counts: z
      .array(
        z.object({
          message_reaction_type_id: z.coerce.string(),
          count: z.number(),
        })
      )
      .optional(),
    reacted_to_by_me_with: z.coerce.string().nullable().optional(),
    sender_profile: z
      .object({
        id: HornetProfileId,
        display_name: z.string(),
        thumbnail_url: z.string(),
      })
      .optional(),
  })
)

const MasterMessageApiPayloadUnion = z.discriminatedUnion("type", [
  ChatMessageApiPayload.merge(MasterMessageApiPayload),
  FavForwardMessageApiPayload.merge(MasterMessageApiPayload),
  HeartMessageApiPayload.merge(MasterMessageApiPayload),
  StickerMessageApiPayload.merge(MasterMessageApiPayload),
  LocationMessageApiPayload.merge(MasterMessageApiPayload),
  PermissionRequestMessageApiPayload.merge(MasterMessageApiPayload),
  PermissionResponseMessageApiPayload.merge(MasterMessageApiPayload),
  SharePhotoMessageApiPayload.merge(MasterMessageApiPayload),
  AwardMessageApiPayload.merge(MasterMessageApiPayload),
  VoiceNoteMessageApiPayload.merge(MasterMessageApiPayload),
  WebViewMessageApiPayload.merge(MasterMessageApiPayload),
  DeletedMessageMessageApiPayload.merge(MasterMessageApiPayload),
])

export const MessageApiPayload = z.object({
  message: MasterMessageApiPayloadUnion,
})
export type MessageApiPayload = z.infer<typeof MessageApiPayload>

export const MessagesConversationMember = z.object({
  id: HornetProfileId,
  display_name: z.string().nullable(),
  account: z.object({
    username: z.string(),
    public: z.boolean(),
  }),
  thumbnail_url: z.string().nullable(),
  explorer: z.boolean(),
  online: z.boolean(),
  status_icon: z.string(),
  last_online: z.coerce.date(),
  last_stung_at: z.coerce.date().nullable().optional(),
  favourite: z.boolean().optional(),
  fan: z.boolean().optional(),
  // The status of the member's request to access the current user's photos
  my_private_photos_access: z.boolean(),
  // The status of the current user's request to access photos
  private_photo_access: MemberPrivatePhotoAccess,
  private_photos_accessible: z.boolean(),
  system_profile: z.boolean(),
  broadcast_profile: z.boolean(),
  verification_level: z.number(),
  last_read_at: z.coerce.date().nullable(),
  quickies_profile_id: QuickiesProfileId.nullable().optional(),
})
export type MessagesConversationMember = z.infer<
  typeof MessagesConversationMember
>

export const MessagesConversationApiPayload = z.object({
  member: MessagesConversationMember,
  inbox: InboxType.nullable(),
  inboxes: z.array(InboxType).nullable(),
  possible_spammer: z.boolean(),
  sending_disabled: SendingDisabledState,
  sending_disabled_reason: SendingDisabledReason,
  messages: z.array(MessageApiPayload),
  pagination: z.object({
    previous: z.string().nullable(), // The `before` parameter to pass to retrieve the set of messages before the current page
  }),
})
export type MessagesConversationApiPayload = z.infer<
  typeof MessagesConversationApiPayload
>

export const MessagesConversationReadReceiptApiPayload = z.object({
  type: z.literal("read"),
  data: z.object({
    profile_id: HornetProfileId,
    last_read_at: z.coerce.date(),
    last_message_id: z.string().nullable(),
    last_message_client_ref: MessageClientRef.nullable(),
  }),
})
export type MessagesConversationReadReceiptApiPayload = z.infer<
  typeof MessagesConversationReadReceiptApiPayload
>

export type MessageModelGroup = {
  messages: MessageModel[]
  showTimestamp: boolean
  isLastReadChunk: boolean
  isSent: boolean
  isRead: boolean
  isLastGroupReceived: boolean
}

export class BareMessageModel {
  readonly payload: BareMessageApiPayload
  protected readonly currentUserProfileId: ProfileId | undefined

  constructor(
    payload: BareMessageApiPayload,
    currentUserProfileId?: ProfileId
  ) {
    this.payload = payload
    this.currentUserProfileId = currentUserProfileId
  }

  get createdAt() {
    return this.payload.created_at
  }

  get messageType() {
    return this.payload.type
  }

  get senderId() {
    switch (this.payload.type) {
      case MessageType.enum.permission_request:
      case MessageType.enum.permission_response:
        return this.payload.data.permission.owner_id
      default:
        return undefined
    }
  }

  get webViewTitle() {
    switch (this.payload.type) {
      case MessageType.enum.web_view:
        return this.payload.data.title
      default:
        return undefined
    }
  }

  get isPermissionGranted() {
    switch (this.payload.type) {
      case MessageType.enum.permission_response:
        return this.payload.data.permission.state === "granted"
      default:
        return undefined
    }
  }

  get data() {
    if (this.payload.type === MessageType.enum.heart) {
      return undefined
    }

    return this.payload.data
  }

  get isSent() {
    return this.senderId === this.currentUserProfileId
  }
}

export class MessageModel extends BareMessageModel {
  private readonly messagePayload: MessageApiPayload["message"]

  constructor(payload: MessageApiPayload, currentUserProfileId?: ProfileId) {
    super(payload.message, currentUserProfileId)
    this.messagePayload = payload.message
  }

  get id() {
    return this.messagePayload.id
  }

  get senderId(): HornetProfileId {
    return this.messagePayload.sender
  }

  get recipientId(): HornetProfileId {
    return this.messagePayload.recipient
  }

  // it's always the other person than who is logged in
  // essentially this will always be conversation id
  get participantId(): ConversationId {
    return this.isSent ? this.recipientId : this.senderId
  }

  get realId() {
    return this.messagePayload.real_id
  }

  get clientRef(): MessageClientRef | null {
    return this.messagePayload.client_ref
  }

  get isDeleted() {
    return this.payload.type === MessageType.enum.deleted_message
  }

  get isRead() {
    return this.messagePayload.state === "read"
  }

  get isEmojiOnly() {
    if (this.payload.type !== MessageType.enum.chat || this.isDeleted) {
      return false
    }

    const emojiRegex =
      /\uD83C\uDFF4(?:\uDB40\uDC67\uDB40\uDC62(?:\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67|\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73|\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74)\uDB40\uDC7F|\u200D\u2620\uFE0F)|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC68(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83D\uDC68|(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|(?:\uD83C[\uDFFB-\uDFFF])\u200D(?:\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3]))|\uD83D\uDC69\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69])|\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|\uD83D\uDC69\u200D\uD83D\uDC66\u200D\uD83D\uDC66|(?:\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2695\u2696\u2708]|\uD83D\uDC68(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|(?:(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)\uFE0F|\uD83D\uDC6F|\uD83E[\uDD3C\uDDDE\uDDDF])\u200D[\u2640\u2642]|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDD6-\uDDDD])(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|\u200D[\u2640\u2642])|\uD83D\uDC69\u200D[\u2695\u2696\u2708])\uFE0F|\uD83D\uDC69\u200D\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D\uDC68(?:\u200D(?:(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D[\uDC66\uDC67])|\uD83C[\uDFFB-\uDFFF])|\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08|\uD83D\uDC69\u200D\uD83D\uDC67|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])\u200D(?:\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|\uD83D\uDC69\u200D\uD83D\uDC66|\uD83C\uDDF6\uD83C\uDDE6|\uD83C\uDDFD\uD83C\uDDF0|\uD83C\uDDF4\uD83C\uDDF2|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])|\uD83C\uDDED(?:\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA])|\uD83C\uDDEC(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE])|\uD83C\uDDEA(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA])|\uD83C\uDDE8(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF])|\uD83C\uDDF2(?:\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF])|\uD83C\uDDF3(?:\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF])|\uD83C\uDDFC(?:\uD83C[\uDDEB\uDDF8])|\uD83C\uDDFA(?:\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF])|\uD83C\uDDF0(?:\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF])|\uD83C\uDDEF(?:\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5])|\uD83C\uDDF8(?:\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF])|\uD83C\uDDEE(?:\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9])|\uD83C\uDDFF(?:\uD83C[\uDDE6\uDDF2\uDDFC])|\uD83C\uDDEB(?:\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7])|\uD83C\uDDF5(?:\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE])|\uD83C\uDDE9(?:\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF])|\uD83C\uDDF9(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF])|\uD83C\uDDE7(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF])|[#*0-9]\uFE0F\u20E3|\uD83C\uDDF1(?:\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE])|\uD83C\uDDE6(?:\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF])|\uD83C\uDDF7(?:\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC])|\uD83C\uDDFB(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA])|\uD83C\uDDFE(?:\uD83C[\uDDEA\uDDF9])|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u261D\u270A-\u270D]|\uD83C[\uDF85\uDFC2\uDFC7]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC70\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDCAA\uDD74\uDD7A\uDD90\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC]|\uD83E[\uDD18-\uDD1C\uDD1E\uDD1F\uDD30-\uDD36\uDDB5\uDDB6\uDDD1-\uDDD5])(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u261D\u26F9\u270A-\u270D]|\uD83C[\uDF85\uDFC2-\uDFC4\uDFC7\uDFCA-\uDFCC]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66-\uDC69\uDC6E\uDC70-\uDC78\uDC7C\uDC81-\uDC83\uDC85-\uDC87\uDCAA\uDD74\uDD75\uDD7A\uDD90\uDD95\uDD96\uDE45-\uDE47\uDE4B-\uDE4F\uDEA3\uDEB4-\uDEB6\uDEC0\uDECC]|\uD83E[\uDD18-\uDD1C\uDD1E\uDD1F\uDD26\uDD30-\uDD39\uDD3D\uDD3E\uDDB5\uDDB6\uDDB8\uDDB9\uDDD1-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])?|(?:[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u270A\u270B\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55]|\uD83C[\uDC04\uDCCF\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF93\uDFA0-\uDFCA\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF4\uDFF8-\uDFFF]|\uD83D[\uDC00-\uDC3E\uDC40\uDC42-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDD7A\uDD95\uDD96\uDDA4\uDDFB-\uDE4F\uDE80-\uDEC5\uDECC\uDED0-\uDED2\uDEEB\uDEEC\uDEF4-\uDEF9]|\uD83E[\uDD10-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD70\uDD73-\uDD76\uDD7A\uDD7C-\uDDA2\uDDB0-\uDDB9\uDDC0-\uDDC2\uDDD0-\uDDFF])|(?:[#*0-9\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA4\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3-\uDEF9]|\uD83E[\uDD10-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD70\uDD73-\uDD76\uDD7A\uDD7C-\uDDA2\uDDB0-\uDDB9\uDDC0-\uDDC2\uDDD0-\uDDFF])\uFE0F/g
    const match = emojiRegex.exec(this.payload.data)

    if (!match) {
      return false
    }

    // emoji detected is the whole content of the message
    return match[0] === this.payload.data
  }

  get hasReactions(): boolean {
    return (
      typeof this.messagePayload.reaction_counts !== "undefined" &&
      this.messagePayload.reaction_counts.length > 0
    )
  }

  get reactions() {
    return this.messagePayload.reaction_counts
  }

  get isReplyable() {
    // can add more checks for message content type etc
    return !!this.messagePayload.real_id
  }

  get isDeletable() {
    return (
      this.messagePayload.is_deletable === true &&
      this.messagePayload.real_id &&
      this.isSent
    )
  }

  get hasContextActions() {
    return this.isDeletable || this.isReplyable
  }

  get hasReply() {
    return !!this.messagePayload.parent_message
  }

  get parentMessage(): MessageModel | undefined {
    const parentMessage = MessageApiPayload.safeParse({
      message: this.messagePayload.parent_message,
    })

    if (parentMessage.success) {
      return new MessageModel(parentMessage.data, this.currentUserProfileId)
    }

    return undefined
  }

  get isSticker() {
    return this.payload.type === MessageType.enum.sticker && this.mediaRouter
  }

  get isChat() {
    return this.payload.type === MessageType.enum.chat
  }

  get isPhoto() {
    return this.payload.type === MessageType.enum.share_photo
  }

  get isLocation() {
    return this.payload.type === MessageType.enum.location
  }

  get isHeartsting() {
    return this.payload.type === MessageType.enum.heart
  }

  get isAward(): boolean {
    return (
      this.payload.type === MessageType.enum.award &&
      !!this.payload.data.award_type_id
    )
  }

  get awardTypeId(): string | undefined {
    return (
      (this.payload.type === MessageType.enum.award &&
        this.payload.data.award_type_id) ||
      undefined
    )
  }

  get isVoiceNote() {
    return (
      this.payload.type === MessageType.enum.voice_note &&
      this.payload.data.playback_url
    )
  }

  get mediaRouter() {
    switch (this.payload.type) {
      case MessageType.enum.sticker:
        return this.payload.data.sticker.media_router
      case MessageType.enum.share_photo:
        return this.payload.data.thumb_retina_url
      default:
        return undefined
    }
  }

  get hasMedia() {
    return (
      this.isSticker || this.isPhoto || this.isLocation || this.isHeartsting
    )
  }

  get sharePhotoUrl() {
    switch (this.payload.type) {
      case MessageType.enum.share_photo:
        return this.payload.data.photo_url
      default:
        return undefined
    }
  }

  get gpsCoords() {
    switch (this.payload.type) {
      case MessageType.enum.location:
        return this.payload.data
      default:
        return undefined
    }
  }

  get permissionGranted() {
    switch (this.payload.type) {
      case MessageType.enum.permission_response:
        return this.payload.data.permission.state === "granted"
      default:
        return undefined
    }
  }

  get sharedMemberId() {
    switch (this.payload.type) {
      case MessageType.enum.fav_forward:
        return this.payload.data.member_id
      default:
        return undefined
    }
  }

  get stickerUrl() {
    switch (this.payload.type) {
      case MessageType.enum.sticker:
        return this.payload.data.sticker.media_router
      default:
        return undefined
    }
  }

  get stickerTitle() {
    switch (this.payload.type) {
      case MessageType.enum.sticker:
        return this.payload.data.sticker.reference
      default:
        return undefined
    }
  }

  get reactedToByMeWith() {
    return typeof this.messagePayload.reacted_to_by_me_with === "string"
      ? this.messagePayload.reacted_to_by_me_with
      : undefined
  }

  get reactionCounts() {
    return this.messagePayload.reaction_counts
  }

  isWithinInterval(message: MessageModel, interval = 180000) {
    return (
      this.payload.created_at.getTime() - message.payload.created_at.getTime() <
      interval
    )
  }

  isRelated(message: MessageModel | undefined) {
    if (typeof message === "undefined") {
      return false
    }

    // that `deleted_message` behaves as `chat`
    const thisCompareType =
      this.payload.type === MessageType.enum.deleted_message
        ? MessageType.enum.chat
        : this.payload.type
    const messageCompareType =
      message.payload.type === MessageType.enum.deleted_message
        ? MessageType.enum.chat
        : message.payload.type

    // only chat message from the same sender can be related
    if (
      message.senderId !== this.senderId ||
      thisCompareType !== MessageType.enum.chat
    ) {
      return false
    }

    // only same type can be related

    if (thisCompareType !== messageCompareType) {
      return false
    }

    // emoji-only message is always standalone
    if (message.isEmojiOnly === true) {
      return false
    }

    // not related after 3 minutes
    return this.isWithinInterval(message)
  }

  showTimestamp(message: MessageModel) {
    return (
      this.isWithinInterval(message) === false ||
      message.senderId !== this.senderId
    )
  }

  serialize() {
    return { message: this.messagePayload }
  }

  addReaction(reactionType: MessageReactionTypeModel) {
    return new MessageModel(
      addReactionToMessagePayload(this, reactionType),
      this.currentUserProfileId
    )
  }

  removeReaction(reactionTypeId: string) {
    return new MessageModel(
      removeReactionFromMessagePayload(this.serialize(), reactionTypeId),
      this.currentUserProfileId
    )
  }

  static createModelWithDefaults(
    payload: CreateBareMessageApiPayload,
    clientRef: MessageClientRef,
    recipientId: HornetProfileId,
    currentUserProfileId: HornetProfileId,
    replyToMessage?: MessageModel
  ) {
    const fakeId = uuidv4()
    return new MessageModel({
      message: {
        ...payload,
        id: fakeId,
        real_id: fakeId,
        client_ref: clientRef,
        recipient: recipientId,
        sender: currentUserProfileId,
        state: MessageReadState.parse("delivered"),
        message_id: fakeId,
        is_deletable: false,
        parent_message: replyToMessage
          ? replyToMessage.serialize().message
          : undefined,
      },
    })
  }

  markAsDeleted() {
    const payload = this.serialize()
    return new MessageModel(
      {
        message: {
          ...payload.message,
          type: MessageType.enum.deleted_message,
          data: null,
        },
      },
      this.currentUserProfileId
    )
  }

  serializeAsLastMessage(): LastMessageApiPayload {
    const serialized = this.serialize()

    return {
      ...serialized.message,
    }
  }
}

function removeReactionFromMessagePayload(
  payload: MessageApiPayload,
  reactionTypeId: string
) {
  const result = { ...payload }
  result.message.reacted_to_by_me_with = null
  result.message.reaction_counts = result.message.reaction_counts || []

  const findByMessageReactionTypeId = propEq(
    "message_reaction_type_id",
    reactionTypeId
  )

  const existing = head(
    result.message.reaction_counts.filter(findByMessageReactionTypeId)
  )

  // for some reason, it does not exist there at all
  if (!existing) {
    return result
  }

  // decrement count to existing reaction type
  if (existing.count > 1) {
    result.message.reaction_counts = [
      ...reject(findByMessageReactionTypeId, result.message.reaction_counts),
      { message_reaction_type_id: reactionTypeId, count: existing.count - 1 },
    ]
    return result
  }

  // remove item altogether
  result.message.reaction_counts = reject(
    findByMessageReactionTypeId,
    result.message.reaction_counts
  )

  return result
}

function addReactionToMessagePayload(
  message: MessageModel,
  reactionType: MessageReactionTypeModel
) {
  // let's work with the raw data first
  let payload = message.serialize()

  if (message.reactedToByMeWith) {
    // don't do anything if we're trying to add the same thing again
    if (message.reactedToByMeWith === reactionType.id) {
      return payload
    }

    // remove the reaction first
    payload = removeReactionFromMessagePayload(
      payload,
      message.reactedToByMeWith
    )
  }

  payload.message.reaction_counts = payload.message.reaction_counts || []

  let count = 1

  const findByMessageReactionTypeId = propEq(
    "message_reaction_type_id",
    reactionType.id
  )

  // add count to existing reaction type
  const existing = head(
    payload.message.reaction_counts.filter(findByMessageReactionTypeId)
  )
  if (existing) {
    count = existing.count + 1
  }

  payload.message.reaction_counts = [
    ...reject(findByMessageReactionTypeId, payload.message.reaction_counts),
    { message_reaction_type_id: reactionType.id, count },
  ]

  payload.message.reacted_to_by_me_with = reactionType.id

  return payload
}

export class MessagesConversationModel {
  private readonly payload: MessagesConversationApiPayload
  protected readonly currentUserProfileId: ProfileId | undefined
  readonly messages: MessageModel[] // mutable via `addMessages`
  readonly member: MessagesConversationMemberModel

  constructor(
    payload: MessagesConversationApiPayload,
    currentUserProfileId?: ProfileId
  ) {
    this.payload = payload
    this.currentUserProfileId = currentUserProfileId
    this.messages = payload.messages.map(
      (message) => new MessageModel(message, currentUserProfileId)
    )
    this.member = new MessagesConversationMemberModel(payload.member)
  }

  get isSendingDisabled() {
    return this.payload.sending_disabled === true
  }

  get isPossibleSpammer() {
    return this.payload.possible_spammer
  }

  get isSystemProfile() {
    return this.payload.member.system_profile === true
  }

  get conversationId() {
    return this.payload.member.id
  }

  get endsWithMessagesFromMe() {
    const chunks = this.groupedMessages

    // only messages from me
    return chunks.length && chunks[chunks.length - 1].messages[0].isSent
  }

  get lastMessage() {
    return this.messages[this.messages.length - 1]
  }

  get isStingable() {
    return isStingable(this.member.lastStungAt)
  }

  get groupedMessages() {
    let groups: MessageModelGroup[] = []

    let lastMessage: MessageModel | undefined
    const messages = this.messages.filter(
      (message) => message.payload.type !== MessageType.enum.web_view
    )

    messages.forEach(function (message) {
      // this defines bubbles and clustering:
      // - unrelated message to previous one (sender, type, read state, etc)
      // - emoji only
      if (
        !lastMessage ||
        !message.isRelated(lastMessage) ||
        message.isEmojiOnly === true
      ) {
        groups.push({
          messages: [],
          showTimestamp: true,
          isLastReadChunk: false,
          isSent: message.isSent,
          isRead: message.isRead,
          isLastGroupReceived: false,
        })
      }

      // don't show timestamp at previous group (close timewise, same sender, etc)
      // check only if current message is a first one in its own group

      // showTimestamp: normally each group (bubble) would have timestamp, but e.g. emoji only
      // has its own bubble (for presenting reasons) where we don't want to show
      // the timestamp (as it would look weird) even though it's related message
      if (
        lastMessage &&
        !message.showTimestamp(lastMessage) &&
        groups[groups.length - 1]["messages"].length === 0
      ) {
        groups[groups.length - 2].showTimestamp = false
      }

      groups[groups.length - 1]["messages"].push(message)

      lastMessage = message
    })

    // filter out empty groups
    groups = groups.filter((group) => group.messages.length)

    // set last read chunk flag for sent messages
    let isLastReadChunkSet = false
    let isLastGroupReceivedSet = false
    groups.reverse().forEach((group, index) => {
      if (!isLastReadChunkSet && group.isSent && group.isRead) {
        groups[index].isLastReadChunk = true
        isLastReadChunkSet = true
      }

      if (!isLastGroupReceivedSet && !group.isSent) {
        groups[index].isLastGroupReceived = true
        isLastGroupReceivedSet = true
      }
    })

    return groups.reverse()
  }

  // mutates content
  addOlderMessages(messages: MessageApiPayload[]) {
    this.messages.unshift(
      ...messages.map(
        (message) => new MessageModel(message, this.currentUserProfileId)
      )
    )

    return this
  }

  hasMessage(messageClientRef: MessageClientRef) {
    return this.messages.some((m) => m.clientRef === messageClientRef)
  }
}

export class MessagesConversationMemberModel {
  private readonly member: MessagesConversationMember

  constructor(payload: MessagesConversationMember) {
    this.member = payload
  }

  get profileId(): HornetProfileId {
    return this.member.id
  }

  get displayName() {
    return this.member.display_name || "The Hornet Guy"
  }

  get displayUsername() {
    return `@${this.member.account.username}`
  }

  get displayNameOrUsername() {
    return this.member.display_name ? this.displayName : this.displayUsername
  }

  get username() {
    return this.member.account.username
  }

  get thumbnailUrl() {
    return this.member.thumbnail_url
  }

  get onlineStatus(): MemberOnlineStatus {
    return { isOnline: this.member.online, lastOnline: this.member.last_online }
  }

  get hasAccessToMyPrivatePhotos() {
    return this.member.my_private_photos_access
  }

  get accessToPrivatePhotos() {
    return this.member.private_photo_access
  }

  get quickiesProfileId(): QuickiesProfileId | null {
    return this.member.quickies_profile_id ?? null
  }

  get lastStungAt() {
    return this.member.last_stung_at
  }
}
